/* eslint-disable react/no-unstable-nested-components */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-arc";
import ReactDOM from "react-dom";
import { Button, Box, Typography } from "@mui/material";

import Constants, { ButtonTitles } from "../../utils/Constants";

const flattenLatLngs = (latLngs) => {
  if (!Array.isArray(latLngs)) return [];
  return latLngs.reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc.push(...flattenLatLngs(item));
    } else if (item && typeof item.lat === "number" && typeof item.lng === "number") {
      acc.push(item);
    }
    return acc;
  }, []);
};

// Tower icon kept minimal so only arc line is visible
const towerIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [0, 0],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

function ArcConnection({
  id,
  from,
  to,
  color = "#9E9E9E",
  baseColor,
  title = "",
  isCompleted: isCompletedProp = false,
  onStatusChange,
  completeColor = "#2E7D32",
}) {
  const map = useMap();
  const layerGroupRef = useRef(null);
  const animationFrameRef = useRef(null);
  const animationMarkerRef = useRef(null);
  const titleMarkerRef = useRef(null);
  const arcLineRef = useRef(null);
  const popupRef = useRef(null);
  const popupContainerRef = useRef(null);
  const activeColorRef = useRef(baseColor || color || "#9E9E9E");

  const [isComplete, setIsComplete] = useState(Boolean(isCompletedProp));

  useEffect(() => {
    setIsComplete(Boolean(isCompletedProp));
  }, [isCompletedProp]);

  useEffect(() => {
    activeColorRef.current = baseColor || color || "#9E9E9E";
  }, [baseColor, color]);

  const log = useCallback((method, message, payload) => {
    if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
      const logger = window.console?.[method];
      if (typeof logger === "function") {
        logger(message, payload);
      }
    }
  }, []);

  const sanitizeLatLng = useCallback((latLng) => {
    if (
      Array.isArray(latLng) &&
      latLng.length === 2 &&
      latLng.every((coord) => coord !== undefined && coord !== null)
    ) {
      const [lat, lng] = latLng.map((coord) =>
        typeof coord === "string" ? parseFloat(coord) : coord
      );
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return [lat, lng];
      }
    }
    return null;
  }, []);

  const sanitizedFrom = useMemo(() => sanitizeLatLng(from), [from, sanitizeLatLng]);
  const sanitizedTo = useMemo(() => sanitizeLatLng(to), [to, sanitizeLatLng]);

  // Determine current color based on completion status
  const currentColor = isComplete ? completeColor : activeColorRef.current;

  const cleanupPopup = useCallback(() => {
    if (popupContainerRef.current) {
      ReactDOM.unmountComponentAtNode(popupContainerRef.current);
      popupContainerRef.current = null;
    }
    popupRef.current = null;
  }, []);

  const getArcMidpointLatLng = useCallback(() => {
    const latLngs = arcLineRef.current?.getLatLngs();
    if (!latLngs) return null;
    const flatLatLngs = flattenLatLngs(latLngs);
    if (flatLatLngs.length === 0) return null;
    return flatLatLngs[Math.floor(flatLatLngs.length / 2)];
  }, []);

  // Handle arc click
  const handleArcClick = useCallback(() => {
    const midpoint = getArcMidpointLatLng();
    if (!midpoint) return;

    // Create popup container
    const popupContainer = document.createElement("div");
    popupContainerRef.current = popupContainer;

    const closePopup = () => {
      if (popupRef.current && map) {
        map.closePopup(popupRef.current);
      } else {
        cleanupPopup();
      }
    };

    // Create React component for popup content
    function PopupContent() {
      const [isSaving, setIsSaving] = useState(false);
      const confirmationMessage = isComplete ? Constants.ARC_UNINSTALL : Constants.ARC_INSTALL;

      const handleConfirm = async () => {
        if (isSaving) return;
        setIsSaving(true);
        const nextIsComplete = !isComplete;

        try {
          let allowClose = true;
          if (typeof onStatusChange === "function") {
            const result = await onStatusChange({
              id,
              isComplete: nextIsComplete,
              from: sanitizedFrom,
              to: sanitizedTo,
              title,
            });
            if (result && typeof result === "object" && "success" in result) {
              allowClose = Boolean(result.success);
            } else {
              allowClose = result !== false;
            }
          }

          if (!allowClose) {
            setIsSaving(false);
            return;
          }

          setIsComplete(nextIsComplete);
          closePopup();
        } catch (error) {
          setIsSaving(false);
        }
      };

      const handleCancel = () => {
        if (isSaving) return;
        closePopup();
      };

      return (
        <Box sx={{ textAlign: "center", padding: "16px", minWidth: "280px" }}>
          <Typography
            variant="h6"
            sx={{
              marginBottom: "12px",
              fontWeight: 600,
              fontSize: "20px",
              color: "#1a1a2e",
            }}
          >
            {confirmationMessage}
          </Typography>
          {title && title.trim() && (
            <Typography
              variant="body2"
              sx={{
                marginBottom: "20px",
                fontSize: "16px",
                color: "#666",
              }}
            >
              {title}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              aria-label={Constants.PROJECT_MAP_CANCEL_ARC_UPDATE_ARIA}
              disabled={isSaving}
              sx={{
                padding: "8px 24px",
                borderColor: "#d0d0d0",
                color: "#666",
                backgroundColor: "#fff",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                "&:hover": {
                  borderColor: "#b0b0b0",
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-disabled": {
                  borderColor: "#d0d0d0",
                  color: "#999",
                },
              }}
            >
              {ButtonTitles.CANCEL}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              aria-label={
                isComplete
                  ? Constants.PROJECT_MAP_CONFIRM_ARC_UNCOMPLETE_ARIA
                  : Constants.PROJECT_MAP_CONFIRM_ARC_COMPLETE_ARIA
              }
              disabled={isSaving}
              sx={{
                padding: "8px 24px",
                backgroundColor: "#1a1a2e",
                color: "#fff",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#0f0f1e",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#42424a",
                  color: "#c7c7c7",
                },
              }}
            >
              {isSaving ? ButtonTitles.SAVING : ButtonTitles.POSITIVE_BUTTON}
            </Button>
          </Box>
        </Box>
      );
    }

    // Render using ReactDOM.render
    ReactDOM.render(<PopupContent />, popupContainer);

    // Create and show popup
    const popup = L.popup({
      closeButton: true,
      autoClose: true,
      closeOnEscapeKey: true,
    })
      .setLatLng(midpoint)
      .setContent(popupContainer)
      .openOn(map);

    // Cleanup on popup close
    popup.on("remove", cleanupPopup);

    popupRef.current = popup;
  }, [
    cleanupPopup,
    getArcMidpointLatLng,
    id,
    isComplete,
    map,
    onStatusChange,
    sanitizedFrom,
    sanitizedTo,
    title,
  ]);

  useEffect(() => {
    if (animationFrameRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (!map || !sanitizedFrom || !sanitizedTo) {
      if (from || to) {
        log("warn", "ArcConnection skipped invalid coordinates", { from, to });
      }
      return undefined;
    }

    if (layerGroupRef.current) {
      map.removeLayer(layerGroupRef.current);
    }

    try {
      const layerGroup = L.layerGroup().addTo(map);
      const arcLine = L.Polyline.Arc(sanitizedFrom, sanitizedTo, {
        color: currentColor,
        weight: 3,
      });

      // Make arc interactive and add click handler
      arcLine.on("click", handleArcClick);
      arcLine.on("mouseover", (e) => {
        const currentZoom = map.getZoom();
        const hoverWeight = currentZoom < 14 ? 8 : currentZoom * 1.2;
        e.target.setStyle({
          weight: hoverWeight,
        });
      });
      arcLine.on("mouseout", (e) => {
        e.target.setStyle({
          weight: 3,
        });
      });

      arcLineRef.current = arcLine;

      const fromTower = L.marker(sanitizedFrom, { icon: towerIcon });
      const toTower = L.marker(sanitizedTo, { icon: towerIcon });
      const arcTitle = typeof title === "string" ? title.trim() : "";

      layerGroup.addLayer(arcLine).addLayer(fromTower).addLayer(toTower);
      layerGroupRef.current = layerGroup;

      if (typeof window !== "undefined") {
        const midpointLatLng = getArcMidpointLatLng();

        if (arcTitle && midpointLatLng) {
          const titleIcon = L.divIcon({
            className: "",
            html: `<div style="
              position: relative;
              transform: translate(-50%, -100%);
              left: 50%;
              padding: 4px 10px;
              color: #39393c;
              font-weight: 50 !important;
              font-family: 'Inter', 'Roboto', sans-serif;
              font-size: 8px;
              border-radius: 8px;
              white-space: nowrap;
            ">
              ${arcTitle}
            </div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          });

          const titleMarker = L.marker(midpointLatLng, {
            icon: titleIcon,
            interactive: false,
          }).addTo(layerGroup);

          titleMarkerRef.current = titleMarker;
        }
      }
    } catch (err) {
      log("error", "ArcConnection error", err);
    }

    return () => {
      if (animationFrameRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      animationMarkerRef.current = null;

      if (popupRef.current && map) {
        map.closePopup(popupRef.current);
        popupRef.current = null;
      }

      cleanupPopup();

      if (layerGroupRef.current) {
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current = null;
      }

      arcLineRef.current = null;
      titleMarkerRef.current = null;
    };
  }, [
    cleanupPopup,
    currentColor,
    from,
    getArcMidpointLatLng,
    handleArcClick,
    log,
    map,
    sanitizedFrom,
    sanitizedTo,
    title,
    to,
  ]);

  useEffect(() => {
    if (!map) return undefined;

    const updateTitleMarkerPosition = () => {
      const midpoint = getArcMidpointLatLng();
      if (midpoint && titleMarkerRef.current) {
        titleMarkerRef.current.setLatLng(midpoint);
      }
    };

    map.on("zoom", updateTitleMarkerPosition);
    map.on("zoomend", updateTitleMarkerPosition);
    map.on("moveend", updateTitleMarkerPosition);

    return () => {
      map.off("zoom", updateTitleMarkerPosition);
      map.off("zoomend", updateTitleMarkerPosition);
      map.off("moveend", updateTitleMarkerPosition);
    };
  }, [getArcMidpointLatLng, map]);

  return null;
}

export default ArcConnection;
